import {
  Component, OnInit, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, ViewChild, ElementRef
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';

import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { ReplaySubject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AppState, Selectors, Actions as RootActions } from 'store/root-reducer';

import { BaseListReqParams } from 'modules/shared/models/base.models';
import { Customer } from 'modules/shared/models/customer.models';
import { PatchCustomerPayload } from 'store/models/customer.models';
import { Domain } from 'modules/shared/models/domain.models';
import { DomainResponse } from 'store/models/domain.models';

import * as CustomerActions from 'store/actions/customer.actions';

import { UserNotificationRow } from 'modules/shared/data-source/user-notifications.data-source';
import { IBreadcrumbs } from 'modules/shared/components/breadcrumbs/breadcrumbs.component';

import { DialogService } from 'modules/shared/components/dialog/dialog.service';
import { DomainService } from 'modules/core/services/domain.service';
import { SnackbarService } from 'app/services/snackbar.service';

export interface IDomainData extends Domain {
  checked?: boolean;
}

@Component({
  selector: 'artemis-customer-access-control-page',
  templateUrl: './customer-access-control.page.html',
  styleUrls: ['./customer-access-control.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerAccessControlPage implements OnInit, OnDestroy {

  @ViewChild('userNotificationsCard', {static: true}) userNotificationsCard: ElementRef;

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject(1);

  // to check if customer should be removed from current domain
  private currentDomain: Domain;
  private totalDomains: Array<IDomainData> = [];
  private customer: Customer;

  loading: boolean = false;
  breadcrumbs: Array<IBreadcrumbs> = [];

  // the number of domains shown when "show less" is clicked
  readonly domainLimit: number = 20;
  isShowAll: boolean = false;

  displayedDomains: Array<IDomainData> = [];
  // to push to user notifications' dropdown
  customerDomains: Array<IDomainData> = [];
  newDomain: Domain;

  constructor(private cdRef: ChangeDetectorRef,
              private domainService: DomainService,
              private dialog: DialogService,
              private snackbar: SnackbarService,
              private store: Store<AppState>,
              private actions$: Actions) { }

  ngOnInit(): void {
    this.getCurrentDomain();
    this.getUpdateDomainActionStatus();

    this.initDomains();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  private loadBreadcrumbs(customerId: number, customerName: string): void {
    this.breadcrumbs = [
      {
        url: '/customers',
        label: 'Customer List'
      }, {
        url: `/customers/${customerId}`,
        label: customerName
      }, {
        url: `/customers/${customerId}/access-control`,
        label: 'Access Control'
      }
    ];
  }

  private getCurrentDomain(): void {
    this.store
      .select(Selectors.domain.getCurrentDomain)
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (res: Domain) => {
          this.currentDomain = res;
        }
      );
  }

  private initDomains(): void {
    const domainsPayload: BaseListReqParams = <BaseListReqParams>{
      offset: 0,
      limit: 999
    };
    this.loading = true;
    this.cdRef.detectChanges();

    combineLatest([
      this.domainService.getAllDomains(domainsPayload),
      this.store.select(Selectors.customer.getCustomer)
    ])
      .pipe(
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        ([domains, customer]: [DomainResponse, Customer]) => {
          if (domains.results && customer && customer.domains) {
            this.customer = customer;
            this.customerDomains = [];
            this.loadBreadcrumbs(customer.id, customer.name);

            this.totalDomains = domains.results.map((domain: Domain) => {
                return <IDomainData>{
                  ...domain,
                  checked: false
                };
              }
            );

            customer.domains.forEach((customerDomain: number) => {
              const index: number = this.totalDomains.findIndex((domain: IDomainData) => domain.id === customerDomain);
              if (~index) {
                this.customerDomains.push(this.totalDomains[index]);
                this.totalDomains[index].checked = true;
              }
            });

            if (this.isShowAll) {
              this.displayedDomains = this.totalDomains.slice();
            } else {
              this.displayedDomains = this.totalDomains.slice(0, this.domainLimit);
            }

            this.loading = false;
            this.cdRef.detectChanges();
          }
        }
      );
  }

  updateDomain(domain: Domain, event: MatCheckboxChange): void {
    const payload: PatchCustomerPayload = {
      customer: Object.assign({}, this.customer),
      redirectToCustomerList: false
    };

    this.loading = true;
    this.cdRef.detectChanges();

    if (event.checked) {
      payload.customer.domains = [...this.customer.domains, domain.id];
      this.newDomain = domain;
      this.store.dispatch(new RootActions.customer.PatchCustomer(payload));
      this.userNotificationsCard.nativeElement.scrollIntoView({behavior: 'smooth'});
    } else {
      payload.customer.domains = this.customer.domains.filter((domainId: number) => domainId !== domain.id);
      if ((domain.id === this.currentDomain.id)) {
        if (this.customer.domains.length <= 1) {
          event.source.toggle();
          this.snackbar.error('At least one domain needs to be selected.');
          this.loading = false;
        } else {
          this.dialog.confirm('Are you sure you want to remove this customer from your current domain?', 'Confirm Domain Update')
          .subscribe((res: 'submit' | 'close') => {
            if (res === 'submit') {
              payload.redirectToCustomerList = true;
              this.store.dispatch(new RootActions.customer.PatchCustomer(payload));
            } else {
              this.loading = false;
              event.source.toggle();
            }
          });
        }
      } else {
        this.newDomain = this.currentDomain;
        this.store.dispatch(new RootActions.customer.PatchCustomer(payload));
      }
    }
  }

  /**
   * Inits observables for getting update domain action status
   * @method getUpdateDomainActionStatus
   */
  private getUpdateDomainActionStatus(): void {
    this.actions$.pipe(
      ofType<CustomerActions.PatchCustomerSuccess>(CustomerActions.CustomerActionTypes.PATCH_CUSTOMER_SUCCESS),
      takeUntil(this.unsubscribe$)
    )
    .subscribe(
      () => {
        this.snackbar.success('Customer access successfully updated');
      }
    );

    this.actions$.pipe(
      ofType<CustomerActions.PatchCustomerFailure>(CustomerActions.CustomerActionTypes.PATCH_CUSTOMER_FAILURE),
      takeUntil(this.unsubscribe$)
    )
    .subscribe(
      (res: CustomerActions.PatchCustomerFailure) => {
        this.snackbar.error(res.payload);
        this.store.dispatch(new RootActions.customer.ClearErrors());
      }
    );
  }

  showAllDomains(): void {
    this.isShowAll = true;
    this.displayedDomains = this.totalDomains;
  }

  showLessDomains(): void {
    this.isShowAll = false;
    this.displayedDomains = this.totalDomains.slice(0, this.domainLimit);
  }

  isAllSelected(): boolean {
    return this.customerDomains.length === this.totalDomains.length;
  }

  isIndeterminate(): boolean {
    return (this.customerDomains.length > 0 && !this.isAllSelected());
  }

  selectAll(event: MatCheckboxChange): void {
    const payload: PatchCustomerPayload = {
      customer: Object.assign({}, this.customer),
      redirectToCustomerList: false
    };

    this.loading = true;
    this.cdRef.detectChanges();

    if (event.checked) {
      this.dialog.confirm('Are you sure you want to grant access of this customer to all users in all domains?',
                          'Confirm Domain Update', true, true)
        .subscribe(
          (res: 'submit' | 'close') => {
            if (res === 'submit') {
              this.displayedDomains = this.totalDomains.slice();
              payload.customer.domains = [...this.displayedDomains.map((selectedDomain: IDomainData) => selectedDomain.id)];
              this.store.dispatch(new RootActions.customer.PatchCustomer(payload));
            } else {
              event.source.checked = false;
              event.source.indeterminate = true;
              this.loading = false;
              this.cdRef.detectChanges();
            }
          }
        );
    } else {
      this.dialog.confirm('Are you sure you want to deselect all domains?\n Please note that your current domain will not be deselected.',
                          'Confirm Domain Update', true, true)
        .subscribe(
          (res: 'submit' | 'close') => {
            if (res === 'submit') {
              payload.customer.domains = [this.currentDomain.id];
              this.store.dispatch(new RootActions.customer.PatchCustomer(payload));
            } else {
              event.source.checked = true;
              this.loading = false;
              this.cdRef.detectChanges();
            }
          }
        );
    }
  }

  updateNotifications(event: Array<UserNotificationRow>): void {
    const payload: PatchCustomerPayload = {
      customer: Object.assign({}, this.customer),
      redirectToCustomerList: false
    };

    payload.customer.users = event.map((user: UserNotificationRow) => user.oauthId);

    this.store.dispatch(new RootActions.customer.PatchCustomer(payload));
  }
}
